import {
  InformedMaterialObligateInputToggleMultiple,
  VALUE_SEPARATOR as INPUT_TOGGLE_VALUE_SEPARATOR,
} from '../InformedMaterialFills/InformedMaterialObligateInputToggleMultiple';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import MultiOptionsComponent from './common/MultiOptions';

// types and classes
import { Component } from './classes';
import {
  QuoteComponentProps,
  QuoteComponentValidationFunctionReturn,
} from './types';
import { ProgramBuilderQuestionInstance } from '../../FormManager/classes';
import { formatAsReadableList } from './utility';

// keep in sync with the QuestionAnswerTypeEnum on the backend
export const OBLIGATE_INPUT_TOGGLE_MULTIPLE_TYPE =
  'ObligateInputToggleMultiple';

const QuoteComponent = function ({
  questionInstance,
  classes = {},
  ...props
}: QuoteComponentProps) {
  const { options, numColumns } = questionInstance.propsBlob;
  const actualNumColumns =
    Number.isNaN(Number(numColumns)) || numColumns === undefined
      ? undefined
      : Number(numColumns);

  return (
    <InformedMaterialObligateInputToggleMultiple
      name={questionInstance.apiName}
      label={questionInstance.getQuoteQuestionLabel()}
      helperText={questionInstance.helpText}
      required={questionInstance.required}
      classes={classes}
      className={classes.obligateInputToggle}
      options={options}
      numColumns={actualNumColumns}
      {...props}
    />
  );
};

export function validateInputToggle(
  questionInstance: ProgramBuilderQuestionInstance,
): QuoteComponentValidationFunctionReturn {
  const errors: Array<string> = [];
  if (!questionInstance.propsBlob) {
    return [
      `Input toggle with name "${questionInstance.generateDisplayName()}" is missing some required attributes.`,
    ];
  }
  const { options } = questionInstance.propsBlob;
  if (!options || options.length === 0) {
    return [
      `Input toggle with name "${questionInstance.generateDisplayName()}" is missing some options. You must provide at least two.`,
    ];
  }
  if (options.length === 1) {
    return [
      `Input toggle with name "${questionInstance.generateDisplayName()}" is missing an option. You must provide at least two.`,
    ];
  }

  // @ts-expect-error
  const optionsWithForbiddenCharacter = options.filter((option) =>
    option.value.includes(INPUT_TOGGLE_VALUE_SEPARATOR),
  );
  if (optionsWithForbiddenCharacter.length === 1) {
    errors.push(
      `The value for the option with label ${optionsWithForbiddenCharacter[0].labels[0].value} has the forbidden character ${INPUT_TOGGLE_VALUE_SEPARATOR}.`,
    );
  } else if (optionsWithForbiddenCharacter.length > 1) {
    errors.push(
      `The values for the options with labels ${formatAsReadableList(
        // @ts-expect-error
        optionsWithForbiddenCharacter.map((option) => option.label),
      )} have the forbidden character ${INPUT_TOGGLE_VALUE_SEPARATOR}.`,
    );
  }

  // ensure that all values are unique
  // @ts-expect-error
  const valueCountMapping = options.reduce((acc, option) => {
    const { value } = option;
    acc[value] = value in acc ? acc[value] + 1 : 1;
    return acc;
  }, {});
  const valuesWithMultiples = Object.entries(valueCountMapping).reduce(
    (acc: Array<string>, [value, count]) => {
      // @ts-expect-error
      if (count > 1) {
        acc.push(value);
      }
      return acc;
    },
    [],
  );
  if (valuesWithMultiples.length > 0) {
    errors.push(
      `Input toggle with API name ${questionInstance.apiName} has ${
        valuesWithMultiples.length == 1 ? 'a value' : 'some values'
      } which appear more than once: ${formatAsReadableList(
        valuesWithMultiples.map((value) => `"${value}"`),
      )}. Each value can appear only once.`,
    );
  }

  if (questionInstance.defaultValue) {
    const values: Array<string> = Array.from(
      // @ts-expect-error
      new Set(options.map((option) => option.value)),
    );
    if (!values.includes(questionInstance.defaultValue)) {
      errors.push(
        `Input toggle with API name ${
          questionInstance.apiName
        } has a default value ("${
          questionInstance.defaultValue
        }") which is not associated with any label. The options are ${formatAsReadableList(
          values.map((value) => `"${value}"`),
        )}.`,
      );
    }
  }

  return errors;
}

const IconComponent = () => {
  return <AllInclusiveIcon fontSize='small' color='primary' />;
};

// @ts-expect-error
const validateValue = (value: string | null | undefined) => {
  if (!(typeof value === 'string')) {
    return 'This field must be filled in';
  }
  if (value.includes(INPUT_TOGGLE_VALUE_SEPARATOR)) {
    return `This field may not contain the special character '${INPUT_TOGGLE_VALUE_SEPARATOR}'`;
  }
};

export function createObligateInputToggleMultipleComponentRegistry() {
  return new Component({
    type: OBLIGATE_INPUT_TOGGLE_MULTIPLE_TYPE,
    typeLabel: 'Input Toggle (Obligate) - Multiple Selections',
    quoteComponent: QuoteComponent,
    dataType: 'string',
    programBuilderComponent: MultiOptionsComponent,
    programBuilderValidation: validateInputToggle,
    icon: IconComponent,
  });
}
